import { create } from 'zustand';
import { BrandingResponse, brandingService } from '@/features/branding/services/branding.service';

interface BrandingState {
  branding: BrandingResponse | null;
  isLoading: boolean;
  loadBranding: () => Promise<void>;
  setBranding: (branding: BrandingResponse) => void;
}

export const useBrandingStore = create<BrandingState>((set) => ({
  branding: null,
  isLoading: true,

  setBranding: (branding) => {
    set({ branding });
    applyBranding(branding);
  },

  loadBranding: async () => {
    set({ isLoading: true });
    try {
      const domain = window.location.hostname;
      const branding = await brandingService.resolveByDomain(domain);
      set({ branding, isLoading: false });
      applyBranding(branding);
    } catch {
      // Domínio não encontrado: usa defaults sem branding customizado
      set({ branding: null, isLoading: false });
    }
  },
}));

/** Converte #RRGGBB / #RGB para componentes HSL usados pelos tokens CSS. */
function hexToHslComponents(hex: string): { h: number; s: number; l: number } {
  const raw = hex.replace('#', '').trim();
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;

  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/** Luminância relativa sRGB — decide texto preto vs branco no primary. */
function relativeLuminance(hex: string): number {
  const raw = hex.replace('#', '').trim();
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;

  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };

  const r = channel(parseInt(full.slice(0, 2), 16));
  const g = channel(parseInt(full.slice(2, 4), 16));
  const b = channel(parseInt(full.slice(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function applyBranding(branding: BrandingResponse) {
  // Cor primária via CSS custom property (formato HSL dos tokens)
  if (branding.primaryColor) {
    const { h, s, l } = hexToHslComponents(branding.primaryColor);
    const root = document.documentElement;
    root.style.setProperty('--primary', `${h} ${s}% ${l}%`);
    root.style.setProperty('--ring', `${h} ${s}% ${l}%`);
    root.style.setProperty(
      '--primary-foreground',
      relativeLuminance(branding.primaryColor) > 0.4 ? '0 0% 0%' : '0 0% 100%',
    );
  }

  // Título da aba
  if (branding.appName) {
    document.title = branding.appName;
  }

  // Favicon
  if (branding.faviconUrl) {
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = branding.faviconUrl;
  }
}
